import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import BrandRibbon from "@/v2/components/BrandRibbon";
import { levelInfo } from "@/v2/lib/gamification";
import { isAdminUser, isDeptManager } from "@/v2/lib/rbac";
import PulseBar, { usePulse, pulseVisible } from "@/v2/components/PulseBar";
import WelcomeApproved from "@/v2/components/WelcomeApproved";
import {
  Home, Map, Trophy, Award, Bell, User, LogOut, Rocket, LifeBuoy, LayoutDashboard, Activity, Building2,
} from "lucide-react";

const links = [
  { to: "/dashboard", label: "الرئيسية", icon: Home, end: true },
  { to: "/journey", label: "رحلة التحديات", icon: Map },
  { to: "/leaderboard", label: "لوحة المتصدرين", icon: Trophy },
  { to: "/badges", label: "الشارات", icon: Award },
  { to: "/notifications", label: "الإشعارات", icon: Bell },
  { to: "/profile", label: "الملف الشخصي", icon: User },
];

export default function V2Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: challenges = [] } = useQuery({ queryKey: ["challenges"], queryFn: async () => (await api.get("/challenges")).data });
  const { data: pulse } = usePulse();
  const pulseOn = pulseVisible(pulse);
  const lvl = levelInfo(user?.points || 0);

  const current = challenges.find((c) => c.status === "in_progress") || challenges.find((c) => c.status === "available");
  const gotoToday = () => (current ? navigate(`/journey/${current.id}`) : navigate("/journey"));
  const doLogout = () => { logout(); navigate("/login"); };

  const navItems = [...links];
  navItems.splice(3, 0, { to: "/pulse", label: "نبض التعلّم", icon: Activity });
  if (isDeptManager(user)) navItems.push({ to: "/dept", label: "لوحة إدارتي", icon: Building2 });
  if (isAdminUser(user)) navItems.push({ to: "/admin", label: "مركز التحكم", icon: LayoutDashboard });
  const isWideArea = pathname.startsWith("/admin") || pathname.startsWith("/dept");

  return (
    <div className={`v2-root workspace-root ${pulseOn ? "pt-[46px]" : ""}`} dir="rtl">
      <PulseBar />
      <WelcomeApproved />
      {/* ambient glows */}
      <div className="fixed top-[-10%] left-[-5%] w-[420px] h-[420px] rounded-full bg-[#91cab8]/10 blur-3xl v2-float pointer-events-none" />
      <div className="fixed bottom-[-10%] right-[10%] w-[380px] h-[380px] rounded-full bg-[#bb9661]/8 blur-3xl v2-float pointer-events-none" style={{ animationDelay: "2s" }} />

      {/* desktop sidebar (right for RTL) */}
      <aside className={`hidden lg:flex flex-col fixed bottom-0 right-0 w-72 p-5 z-30 ${pulseOn ? "top-[46px]" : "top-0"}`}>
        <div className="workspace-sidebar-panel v2-glass rounded-3xl h-full flex flex-col p-5">
          <div className="workspace-brand border-b border-white/10"><BrandLockup tone="dark" stacked /></div>
          <button onClick={gotoToday} data-testid="v2-today-btn" className="v2-btn my-5 w-full">
            <Rocket size={20} /> {current?.status === "in_progress" ? "أكمل تحدي اليوم" : "ابدأ تحدي اليوم"}
          </button>
          <nav className="flex-1 space-y-1 overflow-y-auto">
            {navItems.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={`v2nav-${l.to.slice(1) || "home"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 h-12 rounded-2xl font-semibold transition-all ${
                    isActive ? "bg-gradient-to-l from-[#91cab8]/25 to-[#bb9661]/15 text-white border border-[#bb9661]/30 v2-glow-gold" : "text-[#bcc8cf] hover:text-white hover:bg-white/5"
                  }`}>
                <l.icon size={20} /> {l.label}
              </NavLink>
            ))}
            <button onClick={() => toast.info("للدعم تواصل مع إدارة المنصة: support@masar.sa")} className="w-full flex items-center gap-3 px-4 h-12 rounded-2xl font-semibold text-[#bcc8cf] hover:text-white hover:bg-white/5">
              <LifeBuoy size={20} /> الدعم والمساعدة
            </button>
          </nav>
          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-1 mb-3">
              <div className="relative">
                <img src={user?.avatar} alt={user?.name} className="w-10 h-10 rounded-full object-cover border border-[#bb9661]/40" />
                <span className="absolute -bottom-1 -left-1 text-[9px] font-bold px-1.5 rounded-full bg-[#bb9661] text-[#25343f]">{lvl.index + 1}</span>
              </div>
              <div className="min-w-0">
                <div className="font-bold truncate text-sm">{user?.name}</div>
                <div className="text-xs v2-gold-text font-bold truncate">{lvl.current.name}</div>
              </div>
            </div>
            <button onClick={doLogout} data-testid="v2-logout" className="w-full flex items-center gap-2 justify-center h-11 rounded-2xl text-[#bcc8cf] hover:text-white bg-white/5 border border-white/10 font-semibold">
              <LogOut size={18} /> تسجيل الخروج
            </button>
          </div>
        </div>
      </aside>

      {/* mobile top bar */}
      <header className={`workspace-mobile-header lg:hidden sticky ${pulseOn ? "top-[46px]" : "top-0"} z-30 px-4 h-16 flex items-center justify-between v2-glass rounded-none border-x-0 border-t-0`}>
        <BrandLockup tone="dark" />
        <div className="workspace-mobile-actions flex items-center gap-2">
          {isDeptManager(user) && (
            <button onClick={() => navigate("/dept")} data-testid="v2-mobile-dept" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><Building2 size={18} /></button>
          )}
          {isAdminUser(user) && (
            <button onClick={() => navigate("/admin")} data-testid="v2-mobile-admin" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><LayoutDashboard size={18} /></button>
          )}
          <button onClick={() => navigate("/notifications")} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><Bell size={18} /></button>
          <button onClick={doLogout} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><LogOut size={18} /></button>
        </div>
      </header>

      {/* content */}
      <main className={`workspace-main lg:mr-72 px-4 sm:px-8 pt-6 pb-28 lg:pb-10 mx-auto relative z-10 ${isWideArea ? "max-w-[1400px]" : "max-w-6xl"}`}>
        <BrandRibbon className="workspace-brand-ribbon" />
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <Outlet />
        </motion.div>
      </main>

      {/* mobile bottom nav */}
      <nav className="workspace-mobile-nav lg:hidden fixed bottom-0 inset-x-0 z-40 v2-glass rounded-none border-x-0 border-b-0 px-2 h-[72px] flex items-center justify-around">
        {[links[0], links[1]].map((l) => <BottomItem key={l.to} l={l} />)}
        <button onClick={gotoToday} className="relative -mt-8 w-16 h-16 rounded-full v2-btn v2-pulse !p-0" data-testid="v2-today-fab">
          <Rocket size={26} />
        </button>
        {[links[2], links[5]].map((l) => <BottomItem key={l.to} l={l} />)}
      </nav>
    </div>
  );
}

const BottomItem = ({ l }) => (
  <NavLink to={l.to} end={l.end}
    className={({ isActive }) => `flex flex-col items-center gap-1 text-[11px] font-semibold w-16 ${isActive ? "v2-gold-text" : "text-[#bcc8cf]"}`}>
    <l.icon size={22} /> {l.label.split(" ")[0]}
  </NavLink>
);
